// What a comments host (the document pane) and the editor it hosts tell each
// other (#13194). Context rather than props because the editor sits several
// components below the pane (FpEditor → EditorMain), and only the live
// document's editor is given one: a read-only past version, mounted without
// it, draws no highlights and offers no Comment action.
import { createContext, useContext } from "react";
import type { Editor } from "@tiptap/react";

/** The text a new comment is anchored to: the selection when Comment was
 *  pressed, and its document positions. */
export interface CommentSelection {
  quote: string;
  from: number;
  to: number;
  /** An edit left the quoted text changed while the comment was being
   *  written: the quote is drawn nowhere and the posted thread is not
   *  anchored (it lists as no longer in the document). */
  ended?: boolean;
}

/** A comment being written, as the host holds it: the selection, with an
 *  identity given when the comment is started and kept by its ended copy
 *  (a post anchors and closes only its own draft). */
export type CommentDraft = CommentSelection & { id: string };

export interface EditorCommentsBridge {
  /** Threads drawn as highlights: open ones only. */
  openThreadIds: readonly string[];
  activeThreadId: string | null;
  /** The selection a comment is being written about, while the composer
   *  is open: drawn as a highlight, since the editor's own selection went
   *  with the focus. */
  draft: CommentDraft | null;
  /** The live editor, once created (null when it goes away), so the host
   *  can anchor a new thread, put a colleague's on its quote, and scroll to
   *  a thread's text. */
  onEditor: (editor: Editor | null) => void;
  /** A highlight was clicked: the thread and the highlight's element. */
  onHighlightClick: (threadId: string, element: HTMLElement) => void;
  /** The floating toolbar's Comment on a selection. */
  onStartComment: (selection: CommentSelection) => void;
  /** An edit ended the draft's quote in the editor: the host marks the
   *  draft ended, so a remounted editor does not find its words elsewhere. */
  onDraftEnded: (draft: CommentSelection) => void;
  /** The thread ids whose marks the live document carries, on load and
   *  after every change. */
  onThreadIdsInDocument: (ids: ReadonlySet<string>) => void;
}

/** Marks the comments UI (the composer, the column, the phone sheet): the
 *  editor does not take the focus from a field inside it as it mounts. */
export const COMMENTS_UI_ATTR = "data-comments-ui";

export const EditorCommentsContext = createContext<EditorCommentsBridge | null>(
  null
);

export const useEditorComments = (): EditorCommentsBridge | null =>
  useContext(EditorCommentsContext);
